<template>
  <div class="product container-xl" itemprop="offers" itemscope itemtype="http://schema.org/Offer">
    <meta itemprop="priceCurrency" :content="$store.state.storeView.i18n.currencyCode">
    <meta itemprop="price" :content="parseFloat(product.price_incl_tax).toFixed(2)">
    <meta itemprop="availability" :content="availability">
    <meta itemprop="url" :content="product.url_path">

    <div
      id="product-intro"
      class="row"
    >
      <div class="col-12 col-sm-4">
        <MProductGallery
          :gallery="gallery"
          :configuration="productConfiguration"
          :badges="badges"
          :bundle-options="product.bundle_options"
        />
      </div>

      <div class="col-12 col-sm-8">
        <div class="product__intro">
          <div class="product__header">
            <SfHeading
              :title="product.name | htmlDecode"
              :level="1"
              class="sf-heading--no-underline sf-heading--left"
            />
          </div>

          <div class="row">
            <div class="col-12 col-sm-6">
              <MProductShortInfo
                :product="product"
                :custom-options="productCustomOptions"
                :reviews="reviews"
                :stock="productStock"
              />

              <hr
                v-if="product.bundle_options && product.bundle_options.length > 0"
                class="divider divider-sm"
              />

              <div
                v-if="product.bundle_options && product.bundle_options.length > 0"
                class="product__bundle-info"
              >
                <div class="icon icon-all-in"></div>
                <div class="text">
                  <h3>Inkl. Dämmung & Fußleiste</h3>
                  <p>Dieser Boden wird inkl. Dämmung und Fußleiste geliefert.</p>
                </div>
              </div>

              <hr class="divider divider-sm" />

              <MProductLinks
                :product="product"
              />
            </div>

            <div class="col-12 col-sm-6">
              <div
                class="product__price-and-availability"
                v-if="!product.bundle_options || product.bundle_options.length === 0"
              >
                <!--<div class="product-availability">
                  <OnlineAvailability
                    :product="product"
                    :qty="stock.max"
                    :threshold-qty="stock.thresholdQuantity"
                  />
                  <StoreAvailability
                    :product="product"
                    :qty="stock.max"
                    :qty-pickup="stock.maxPickup"
                    :show-modal-button="true"
                    :show-not-selected="true"
                    :threshold-qty="stock.thresholdQuantity"
                  />
                </div>-->

                <AProductPrice
                  v-if="product.type_id !== 'grouped'"
                  :product="product"
                  :custom-options="customOptions"
                />
              </div>

              <MProductConfigurator
                v-if="product.bundle_options && product.bundle_options.length > 0"
                :product="product"
              />

              <div
                :class="{ 'product__buy-box': true, 'sticky': showStickyHeader }"
              >
                <div class="product__buy-box__inner">
                  <div class="badge badge-gray">Menge wählen</div>

                  <MProductOptionsConfigurable
                    v-if="product.type_id =='configurable'"
                    :product="product"
                    :configuration="productConfiguration"
                  />
                  <MProductOptionsGroup
                    v-if="product.type_id =='grouped'"
                    :product-options="product.product_links"
                  />

                  <div class="sticky-container">
                    <div class="sticky-nav">
                      <div class="product-info">
                        <SfImage
                          v-for="(item, i) in gallery.slice(0, 1)" :key="i"
                          class="product-image"
                          :src="item.mobile.url"
                          :alt="item.alt"
                          width="32"
                          height="32"
                        />
                        <div class="product-name">{{ product.name | htmlDecode }}</div>
                      </div>
                      <AStickyNav :sections="[
                        { id: 'product-intro', name: 'Fotos' },
                        { id: 'product-description', name: 'Beschreibung' },
                        { id: 'product-config', name: 'Leisten & Dämmung konfigurieren', enabled: typeof product.bundle_options !== 'undefined' && product.bundle_options.length > 0 },
                        { id: 'product-datasheet', name: 'Datenblatt' },
                        { id: 'product-reviews', name: 'Bewertungen' },
                      ]" />
                    </div>

                    <div>
                      <div
                        v-if="product.bundle_options && product.bundle_options.length > 0"
                        class="product__buy-box__qty"
                      >
                        <AProductQuantityCalculator
                          @input="firstOptionQuantity = $event"
                          unlimit-quantity
                          v-if="firstOptionValue && firstOptionValue.product"
                          :product="firstOptionValue.product"
                          :showOffcuts="true"
                        />

                        <div class="total">
                          <div>Gesamtpreis:</div>
                          <SfPrice
                            :regular="totalPrice"
                          />
                        </div>
                      </div>

                      <MProductAddToCart
                        class="product__add-to-cart"
                        :product="product"
                        :stock="productStock"
                        ref="addToCart"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="row"
      ref="stickyHeaderThreshold"
    >
      <div class="col-12 col-sm-6">
        <div
          id="product-description"
          class="product__more-info description"
        >
          <SfHeading
            :title="$t('Description')"
            :level="4"
            class="sf-heading--left"
          />
          <div
            itemprop="description"
            :class="product.description && product.description.includes('pagebuilder') ? 'pagebuilder-container' : ''"
            v-html="product.description"
          />

          <!--MProductAdditionalInfo
            :product="product"
            :reviews="reviews"
            :attributes="productAttributes"
            ref="additionalInfo"
          /-->
        </div>
      </div>

      <div class="col-12 col-sm-6">
        <div
          id="product-config"
          class="product__info"
        >
          <SfButton
            v-show="sizeOption"
            @click.native="openSizeGuide"
            class="sf-button--text desktop-only product__guide"
          >
            {{ $t('Size guide') }}
          </SfButton>

          <MProductOptionsBundleRz
            v-if="product.bundle_options && product.bundle_options.length > 0"
            :product="product"
            ref="bundleOptions"
          />
          <MProductOptionsCustom
            v-else-if="product.custom_options && product.custom_options.length > 0"
            :product="product"
          />
        </div>
      </div>
    </div>

    <hr class="divider" />

    <div
      v-if="productAttributes.length"
      id="product-datasheet"
      class="product__more-info datasheet"
    >
      <SfHeading
        :title="$t('Additional Information')"
        :level="3"
        class="sf-heading--left"
      />
      <div class="datasheet-items">
        <AProductAttribute
          v-for="(attribute, i) in productAttributes"
          :key="i"
          :product="product"
          :attribute="attribute"
          class="product__property"
        />
      </div>
    </div>

    <hr class="divider" />

    <div
      id="product-reviews"
      ref="productReviews"
      class="product__more-info reviews"
    >
      <SfHeading
        :title="$t('Reviews')"
        :level="3"
        class="sf-heading--left"
      />

      <div class="row">
        <div class="col-12 col-md-3">
          <div class="review-header">
            <AProductRating
              @click="handleOpenReviewModal"
              :reviews="reviews"
            >
              {{ $t('Leave a review') }}
            </AProductRating>
          </div>
        </div>
        <div class="col-12 col-md-9">
          <SfHeading
            :title="reviewsCount + ' ' + $t('Reviews')"
            :level="4"
            class="sf-heading--left"
          />
          <MReviewList v-show="reviewsCount" :reviews="reviews" :product="product" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import get from 'lodash-es/get'
import { mapActions } from 'vuex';
import { mapGetters } from 'vuex';
import { SfHeading, SfButton, SfPrice, SfImage } from '@storefront-ui/vue';
import MProductGallery from 'theme/components/molecules/m-product-gallery';
import MProductShortInfo from 'theme/components/molecules/m-product-short-info';
import MProductAddToCart from 'theme/components/molecules/m-product-add-to-cart';
import MProductLinks from 'theme/components/molecules/m-product-links';
import MProductAdditionalInfo from 'theme/components/molecules/m-product-additional-info';
import MProductOptionsConfigurable from 'theme/components/molecules/m-product-options-configurable';
import MProductOptionsBundle from 'theme/components/molecules/m-product-options-bundle';
import MProductOptionsBundleRz from 'theme/components/molecules/m-product-options-bundle-rz';
import MProductOptionsCustom from 'theme/components/molecules/m-product-options-custom';
import MProductOptionsGroup from 'theme/components/molecules/m-product-options-group';
import { ModalList } from 'theme/store/ui/modals';
import MProductConfigurator from 'theme/components/molecules/m-product-configurator';
import { prepareBadges } from 'theme/helpers';
import AProductAttribute from 'theme/components/atoms/a-product-attribute';
import AProductQuantityCalculator from 'theme/components/atoms/a-product-quantity-calculator'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle';
import { price } from '@vue-storefront/core/filters/price'
import { getProductPrice } from 'theme/helpers'
import AProductRating from 'theme/components/atoms/a-product-rating';
import AStickyNav from 'theme/components/atoms/a-sticky-nav';
import MReviewList from 'theme/components/molecules/m-review-list';
import moment from 'moment';
import AProductPrice from 'theme/components/atoms/a-product-price';
import StoreAvailability from 'src/modules/vsf-inventory-sources/components/StoreAvailability'
import OnlineAvailability from 'src/modules/vsf-inventory-sources/components/OnlineAvailability';

export default {
  components: {
    SfButton,
    SfHeading,
    SfPrice,
    SfImage,
    MProductGallery,
    MProductShortInfo,
    MProductAddToCart,
    MProductLinks,
    MProductAdditionalInfo,
    MProductOptionsConfigurable,
    // MProductOptionsBundle,
    MProductOptionsBundleRz,
    MProductOptionsCustom,
    MProductOptionsGroup,
    MProductConfigurator,
    AProductAttribute,
    AProductQuantityCalculator,
    AProductRating,
    AStickyNav,
    MReviewList,
    AProductPrice,
    StoreAvailability,
    OnlineAvailability
  },
  data () {
    return {
      firstOptionQuantity: 1,
      showStickyHeader: false
    }
  },
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    productGallery: {
      type: Array,
      default: () => []
    },
    productConfiguration: {
      type: Object,
      default: () => ({})
    },
    productCustomOptions: {
      type: Object,
      default: () => ({})
    },
    productAttributes: {
      type: Array,
      default: () => []
    },
    productStock: {
      type: Object,
      default: () => ({})
    }
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll)
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll)
  },
  watch: {
    firstOptionQuantity (newVal) {
      this.$refs.bundleOptions.$children[0].quantity = newVal
    }
  },
  computed: {
    ...mapGetters({
      getCurrentCustomOptions: 'product/getCurrentCustomOptions'
    }),
    gallery () {
      return this.productGallery.map(imageObject => ({
        id: imageObject.id,
        mobile: {
          url: imageObject.src,
          alt: this.product.name
        },
        desktop: {
          url: imageObject.src,
          alt: this.product.name
        }
      }));
    },
    reviews () {
      const baseReviews = get(this.$store.state.review, 'items.items', [])
      if ( baseReviews ) {
        return baseReviews.map((review) => ({
          author: review.nickname,
          date: moment(review.created_at).format('DD.MM.YYYY'),
          title: review.title,
          message: review.detail,
          ratings: review.ratings,
          rating: this.calculateRatingScore(review.ratings)
        }))
      } else {
        return []
      }
    },
    availability () {
      return this.product.stock && this.product.stock.is_in_stock ? 'InStock' : 'OutOfStock'
    },
    sizeOption () {
      return get(this.productConfiguration, 'size', false)
    },
    badges () {
      return prepareBadges(this.product)
    },
    firstOptionValue () {
      return this.selectedOptionValue(this.firstOption())
    },
    price () {
      return getProductPrice(this.product, this.customOptions)
    },
    totalPrice () {
      var total = null
      var qty = 1

      for (const option of this.bundleOptions) {
        if (total === null) { // first option uses bundle product price
           total = (this.price.special ? this.price.special : this.price.regular)
           qty = this.selectedOptionValue(option).qty
           total *= qty
           continue
        }

        if (!this.selectedOptionValue(option).product) {
          continue;
        }
        var optionPrice = this.selectedOptionValue(option).price;
        if (!isNaN(optionPrice)) {
          total = total +  (optionPrice * this.selectedOptionValue(option).qty)
        }
      }

      return price(this.firstOptionValue.qty * (total / qty))
    },
    bundleOptions () {
      return this.product.bundle_options.filter(o => this.selectedOptionValue(o))
    },
    reviewsCount () {
      return this.reviews.length;
    }
  },
  methods: {
    ...mapActions('ui', {
      openModal: 'openModal'
    }),
    openSizeGuide () {
      this.openModal({ name: ModalList.SizeGuide })
    },
    selectedOptionValue (option) {
      return getSelectedBundleOptionValue(this.product, option)
    },
    firstOption () {
      return this.product.bundle_options.find(() => true)
    },
    onScroll () {
      var threshold = this.$refs.stickyHeaderThreshold.offsetTop

      if ( threshold < window.top.scrollY ) {
        this.showStickyHeader = true
      } else {
        this.showStickyHeader = false
      }
    },
    handleOpenReviewModal () {
      this.openModal({ name: ModalList.Review, payload: this.product.id })
    },
    calculateRatingScore (ratings) {
        let combinedRatings = ratings.reduce((sum, rev) => (sum + (rev.value || 0)), 0)
        let score = combinedRatings ? combinedRatings / ratings.length : combinedRatings
        return parseFloat((Math.round((score) * 10) / 10).toFixed(1))
    }
  }
};
</script>
<style lang="scss" scoped>
@import "~@storefront-ui/shared/styles/helpers/breakpoints";

.product {
  &__add-to-cart {
    margin: var(--spacer-base) 0 0;
    @include for-desktop {
      margin-top: var(--spacer-sm);
    }
  }
  &__guide,
  &__compare,
  &__save {
    display: block;
    margin: var(--spacer-xl) 0 var(--spacer-base) auto;
  }
}

.section {
  border-bottom: 1px solid #f1f2f3;
  padding-bottom: 10px;
  @include for-desktop {
    border: 0;
    padding-bottom: 0;
  }
}
</style>
